import React, { useEffect } from 'react';
import { Box, Typography, Container, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  GlassColors,
  GlassShadows,
  NeumorphicButton,
} from '../../components/glassmorphism/GlassmorphismStyles';

import { ReactComponent as CaseIcon } from '../../assets/icons/info/caseIcon.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { StepsPath } from './StepsPath';
import { trackEvent } from '../../analytics';
import { BenefitsBlock } from './BenefitsBlock';

const EarlyBirdPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  useEffect(() => {
    trackEvent('early_bird_page_view');
  }, []);

  const goHandymanSignup = () => {
    navigate('/sign-up?role=handyman', { state: { from: location.pathname } });
  };

  return (
    <Container
      sx={{
        pt: { xs: '96px', sm: '136px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '956px',
          maxWidth: '100%',
          gap: { xs: '24px', sm: '48px' },
          px: 0,
          mb: { xs: '24px', sm: '48px' },
          mx: 'auto',
        }}
      >
        {/* Heading Section */}
        <BoxInfoWrapper>
          <Heading sx={{ mb: '24px' }} variant='h1'>
            EXCLUSIVE OFFER FOR THE FIRST 300 UK PROFESSIONALS
          </Heading>
          <Typography
            sx={{
              fontFamily: 'Rodoto, sans-serif',
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              fontWeight: 700,
              fontSize: { xs: '16px', tablet: '20px' },
              lineHeight: '175%',
              letterSpacing: '.15px',
              textAlign: 'center',
              mb: { xs: '24px', tablet: '32px' },
            }}
          >
            We are launching Womapp - a zero-risk platform where tradespeople
            are respected, not exploited. The first 300 professionals to join
            will never pay a platform fee, securing a lifetime 0% commission
            rate.
          </Typography>
          <NeumorphicButton
            sx={{
              display: 'flex',
              gap: '8px',
              m: '0 auto 16px',
              fontSize: '16px',
            }}
            onClick={() => goHandymanSignup()}
            data-ga-label='Create Your Free Profile'
          >
            <CaseIcon />
            Claim My Lifetime 0% Slot
          </NeumorphicButton>
          <HelperText>Takes 2 minutes. No credit card required.</HelperText>
        </BoxInfoWrapper>

        {/* Benefits Block Section */}
        <BoxInfoWrapper>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              fontWeight: { xs: 700, tablet: 600 },
              fontSize: { xs: 24, tablet: 34 },
              lineHeight: { xs: '133%', tablet: '124%' },
              letterSpacing: { xs: 0, tablet: '.25px' },
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              mb: { xs: '24px', tablet: '16px' },
              maxWidth: { xs: 352, tablet: 'none' },
              mx: 'auto',
            }}
          >
            The Directory Model is Broken. Here is the Fix.
          </Typography>
          <BenefitsBlock isDark={isDark} />
          <NeumorphicButton
            id='btn-reg-2'
            sx={{
              display: 'flex',
              gap: '8px',
              m: '32px auto 16px',
              fontSize: '16px',
            }}
            onClick={() => goHandymanSignup()}
            data-ga-label='Join as a Pro'
          >
            <CaseIcon />
            Secure My Early Bird Status
          </NeumorphicButton>
          <HelperText>Setup takes less than 2 minutes.</HelperText>
        </BoxInfoWrapper>

        {/* Path to Start Section */}
        <BoxInfoWrapper>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              fontWeight: { xs: 700, tablet: 600 },
              fontSize: { xs: 24, tablet: 34 },
              lineHeight: { xs: '133%', tablet: '124%' },
              letterSpacing: { xs: 0, tablet: '.25px' },
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              mb: { xs: '24px', tablet: '32px' },
            }}
          >
            Your path to getting local jobs
          </Typography>
          <StepsPath isDark={isDark} />
          <NeumorphicButton
            id='btn-reg-3'
            sx={{
              display: 'flex',
              gap: '8px',
              m: '32px auto 16px',
              fontSize: '16px',
            }}
            onClick={() => goHandymanSignup()}
            data-ga-label='Start Getting Jobs'
          >
            <CaseIcon />
            Start Getting Jobs
          </NeumorphicButton>
          <HelperText>Free to join. Free to quote.</HelperText>
        </BoxInfoWrapper>

        <BoxInfoWrapper
          sx={{
            boxShadow: `
              14.15px 14.15px 22.64px -0.37px #00FFFF21,
              -17.54px -17.54px 28.66px 0px #A6FFFF30,
              10px 10px 28.66px -11.7px #00929233,
              1.58px 1.58px 1.7px 0px #A3FFFF inset,
              -2.07px -2.64px 4.52px -1.51px #00929252 inset
            `,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              fontWeight: 700,
              fontSize: 24,
              lineHeight: '133%',
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              mb: '24px',
            }}
          >
            Secure Your Lifetime 0% Rate Before It’s Gone.
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Rodoto, sans-serif',
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              fontSize: { xs: '16px' },
              lineHeight: '150%',
              letterSpacing: '.15px',
              textAlign: 'center',
              msxWidth: '722px',
              mb: { xs: '24px', tablet: '32px' },
              mx: 'auto',
            }}
          >
            This is your only opportunity to join the UK's fairest services
            marketplace and <strong>eliminate commission costs forever.</strong>{' '}
            As one of our first 300 Early Birds, you get guaranteed upfront
            payments, direct client communication, and a{' '}
            <strong>lifetime 0% commission rate.</strong> While other
            tradespeople keep paying directories for dead leads and chasing
            unpaid invoices, you will be growing your business completely
            risk-free and keeping{' '}
            <strong>100% of your hard-earned money.</strong>
          </Typography>
          <NeumorphicButton
            id='btn-reg-4'
            sx={{
              display: 'flex',
              gap: '8px',
              m: '0 auto 16px',
              fontSize: '16px',
            }}
            onClick={() => goHandymanSignup()}
            data-ga-label='Join as a Professional'
          >
            <CaseIcon />
            Join as a Professional
          </NeumorphicButton>
          <HelperText sx={{ p: '0 20px' }}>
            Strictly limited to 300 professionals.
          </HelperText>
        </BoxInfoWrapper>
      </Box>
    </Container>
  );
};

// BoxInfoWrapper
const BoxInfoWrapper = styled(Box)(({ theme }) => ({
  padding: '32px 95px',
  borderRadius: '64px',
  boxShadow: GlassShadows.cardShadowDefault,

  [theme.breakpoints.down('md')]: {
    padding: '32px 24px',
  },
}));

// Heading
const Heading = styled(Typography)(({ theme }) => ({
  fontFamily: 'Montserrat, sans-serif',
  textTransform: 'uppercase',
  fontWeight: 500,
  fontSize: '48px',
  lineHeight: '117%',
  textAlign: 'center',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '32px',
    lineHeight: 1.24,
    letterSpacing: '.25px',
    fontWeight: 600,
  },
}));

// Helper text
const HelperText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
  letterSpacing: '.4px',
  fontSize: '12px',
  lineHeight: '166%',
  color:
    theme.palette.mode === 'dark'
      ? GlassColors.dark.textSecondary
      : GlassColors.light.textSecondary,
  textAlign: 'center',
}));

export default EarlyBirdPage;
